import { Component, OnInit, inject, AfterViewInit } from '@angular/core';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { AuthService } from '../../../auth/services/auth.service';
import { DashboardArcgisMapsComponent } from '../dashboard-arcgis-maps/dashboard-arcgis-maps.component';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { DashboardLeadsStatusComponent } from '../dashboard-leads-status/dashboard-leads-status.component';
import { SmeDashboardComponent } from '../sme-dashboard/sme-dashboard.component';
import { DashboardPendingActionsComponent } from '../dashboard-pending-actions/dashboard-pending-actions.component';
import { DashboardDeeprootsTabsComponent } from '../dashboard-deeproots-tabs/dashboard-deeproots-tabs.component';
import { SplashScreenService } from '../../../../shared/services/splash-screen/splash-screen.service';
import { LeadsService } from '../../../leads/services/leads.service';
import { takeUntil, exhaustMap, takeWhile } from 'rxjs/operators';
import { timer, Subscription } from 'rxjs';
import { UpdateLeadsPopupComponent } from '../../../update-leads-popup/components/update-leads-popup/update-leads-popup.component';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { DashboardMetrics } from '../../models/dashboard-metrics';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    DashboardArcgisMapsComponent,
    DashboardLeadsStatusComponent,
    SmeDashboardComponent,
    DashboardPendingActionsComponent,
    DashboardDeeprootsTabsComponent,
    UpdateLeadsPopupComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent
  extends MenuComponentBase
  implements OnInit, AfterViewInit
{
  _authService = inject(AuthService);
  splashScreenService = inject(SplashScreenService);
  private leadsService = inject(LeadsService);
  private dashboardService = inject(DashboardService);
  private commonService = inject(CommonService);

  showLeadsList: boolean = this._authService.hasPermission(
    PermissionCode.GET_SET_LEADS,
  );
  hasDashboardTabsAccess: boolean =
    this._authService.hasPermission(PermissionCode.GET_SET_LEADS) ||
    this._authService.hasPermission(PermissionCode.ACCESS_DEEPROOTS) ||
    this._authService.hasPermission(PermissionCode.MANAGE_PUBLIC_LINKS) ||
    this._authService.hasPermission(PermissionCode.MANAGE_MATCHING_CRITERIA);

  readonly isSmeUser = this._authService.isSMEUser();

  showToast = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  showLeadStatusPopup = false;
  isLoadingDashboardMetrics = true;
  dashboardMetrics: DashboardMetrics | null = null;
  private pollingSub: Subscription | null = null;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.splashScreenService.hide();
    }, 2000);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (!this.isSmeUser) {
      this.loadDashboardMetrics();

      this.leadsService.statusCountRefresh$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadDashboardMetrics();
        });
    }
  }

  private loadDashboardMetrics(): void {
    this.isLoadingDashboardMetrics = true;
    this.dashboardService
      .getDashboardMetrics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (metrics) => {
          this.dashboardMetrics = metrics;
          this.isLoadingDashboardMetrics = false;
        },
        error: () => {
          this.isLoadingDashboardMetrics = false;
        },
      });
  }

  onRefreshMetrics(): void {
    if (this.isLoadingDashboardMetrics) return;

    this.isLoadingDashboardMetrics = true;
    this.dashboardService
      .recalculateDashboardMetrics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 'COOLDOWN') {
            this.isLoadingDashboardMetrics = false;
            this.commonService.triggerToast({
              type: ToastType.Success,
              message: 'Dashboard metrics are already up to date.',
            });
            return;
          }

          if (response.status === 'IN_PROGRESS') {
            this.startPollingRecalculateStatus();
          } else if (response.status === 'COMPLETED') {
            this.loadDashboardMetrics();
            this.commonService.triggerToast({
              type: ToastType.Success,
              message: 'Dashboard metrics updated successfully.',
            });
          }
        },
        error: () => {
          this.isLoadingDashboardMetrics = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to initiate metrics recalculation.',
          });
        },
      });
  }

  private startPollingRecalculateStatus(): void {
    this.pollingSub?.unsubscribe();

    this.pollingSub = timer(1000, 5000)
      .pipe(
        exhaustMap(() => this.dashboardService.getRecalculateStatus()),
        takeWhile((res) => res.status === 'IN_PROGRESS', true),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (statusRes) => {
          if (statusRes.status === 'COMPLETED') {
            this.loadDashboardMetrics();
            this.commonService.triggerToast({
              type: ToastType.Success,
              message: 'Dashboard metrics recalculated successfully.',
            });
          } else if (statusRes.status === 'FAILED') {
            this.isLoadingDashboardMetrics = false;
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: 'Metrics recalculation failed.',
            });
          }
        },
        error: () => {
          this.isLoadingDashboardMetrics = false;
        },
      });
  }

  onLeadsToastMessage(event: { message: string; type: 'success' | 'error' }) {
    this.commonService.triggerToast({
      type: event.type === 'success' ? ToastType.Success : ToastType.Error,
      message: event.message,
    });
  }
}
